import React, { useEffect, useState } from "react";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { observer } from "mobx-react-lite";
import * as Yup from "yup";
import {
    htmlValidation,
    makeid_alpa_numeric,
    TextError,
    useStore,
    uploadBlob,
    uploadFile,
} from "../../../helpers";
import { toast } from "react-toastify";
import VoterReachoutAudioPlayer from "./voter-reachout-audioplayer";
import { useParams } from "react-router-dom";
import { FormButtons, VoiceRecorder, LoadingState } from "../../../components";
import { toJS } from "mobx";

const AddEditVoiceCall = observer((props: any) => {
    const rootStore = useStore();
    const { projectid } = useParams();
    const [activeTab, setActiveTab] = useState<"record" | "upload">("record");
    const [voiceSubmit, setVoiceSubmit] = useState(false);
    const [audio_response, setAudioResponse] = useState<string | null>(null);
    const [buffer, setBuffer] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedFileName, setSelectedFileName] = useState<string | null>(null);

    const { voterReachOutStore } = rootStore;
    const { formType, voiceMessageFormValue, messageType } = voterReachOutStore;
    const { formSubmit } = props;

    const path = `project-${projectid}/voterreachout/voicemessage`;

    const voiceMessageFormInitialValues = {
        name: voiceMessageFormValue.name || "",
    };

    const voiceMessageValidationSchema = Yup.object().shape({
        name: Yup.string().required("Campaign name is required"),
    });

    const onRecorderSubmit = async (recordedOutput: Blob) => {
        setLoading(true);
        const uploadPath = `project-${projectid}/voterreachout/voicemessages`;
        try {
            const id = makeid_alpa_numeric(8);
            const medianame = `${id}.mp4`;
            const audio_res = await uploadBlob(uploadPath, medianame, recordedOutput);
            setAudioResponse(audio_res.key);
            setBuffer([]);
            setSelectedFileName("Live Recording.mp4");
            toast.dismiss();
            toast.success("Recording uploaded successfully");
        } catch (e) {
            console.error("Something went wrong", e);
            toast.error("Audio upload failed");
        } finally {
            setLoading(false);
        }
    };

    const deleteAudioResponse = () => {
        setAudioResponse(null);
        setBuffer([]);
        setSelectedFileName(null);
        setVoiceSubmit(false);
    };

    const handleAudioFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        htmlValidation(e);
        const selectedFile = e.target.files?.[0];
        if (!selectedFile) return;

        if (
            !selectedFile.type.includes("audio") &&
            !["audio/mpeg", "audio/wav", "audio/mp3"].includes(selectedFile.type)
        ) {
            toast.error("Please upload a valid audio file (MP3 or WAV)");
            return;
        }

        setLoading(true);
        try {
            const s3Path = await uploadFile(path, { file: selectedFile }, "urn-assets");

            if (s3Path) {
                const file_type = selectedFile.type;
                const id = makeid_alpa_numeric(8);

                setBuffer([{ path: s3Path, type: file_type, id }]);
                setAudioResponse(s3Path);
                setSelectedFileName(selectedFile.name);

                toast.success("Audio uploaded successfully");
            }
        } catch (err) {
            console.error("Upload failed:", err);
            toast.error("File upload failed. Try again.");
        } finally {
            setLoading(false);
        }
    };

    const voiceMessageFormSubmit = async (values: any) => {
        setVoiceSubmit(true);

        if (loading) {
            toast.info("Please wait, audio is still uploading...");
            return;
        }

        if (!audio_response && buffer.length === 0) {
            toast.error("Please record or upload an audio file before submitting");
            return;
        }

        const voicePayload = {
            projectid,
            channel: "voicecall",
            more: {},
            ds: {
                name: values.name,
                text: audio_response,
                attachements: buffer,
            },
        };

        formSubmit(voicePayload, voiceMessageFormValue.id);
    };

    useEffect(() => {
        if (messageType === "Voice Messages" && formType === "update") {
            const plainData = toJS(voiceMessageFormValue);
            setAudioResponse(plainData.message);
            setBuffer(plainData.attachements || []);
            if (plainData.message) setSelectedFileName("Existing Audio Message");
        }
    }, [messageType, formType, voiceMessageFormValue]);

    const isAudioReady = Boolean(audio_response || buffer.length > 0);

    return (
        <div className="relative mx-auto max-w-2xl">
            {loading && <LoadingState />}

            <Formik
                initialValues={voiceMessageFormInitialValues}
                onSubmit={voiceMessageFormSubmit}
                validationSchema={voiceMessageValidationSchema}
                enableReinitialize
            >
                <Form
                    className="space-y-6 text-left"
                    placeholder={undefined}
                    onPointerEnterCapture={undefined}
                    onPointerLeaveCapture={undefined}
                >
                    {/* Header & Title Field */}
                    <div className="space-y-2">
                        <label htmlFor="name" className="block text-sm font-semibold text-gray-800">
                            Message Title <span className="text-red-500">*</span>
                        </label>
                        <Field
                            type="text"
                            id="name"
                            name="name"
                            placeholder="e.g. Campaign Update Announcement"
                            className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm shadow-sm transition-all placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                        <ErrorMessage name="name" component={TextError} />
                    </div>

                    {/* Mode Switcher Tabs */}
                    <div className="space-y-1">
                        <label className="block text-sm font-semibold text-gray-800">
                            Audio Content <span className="text-red-500">*</span>
                        </label>
                        <p className="text-xs text-gray-500">
                            Choose to record live or upload an existing audio file.
                        </p>

                        <div className="grid grid-cols-2 rounded-lg bg-gray-100 p-1 text-sm font-medium text-gray-600">
                            <button
                                type="button"
                                onClick={() => setActiveTab("record")}
                                className={`flex items-center justify-center gap-2 rounded-md px-3 py-2 transition-all ${
                                    activeTab === "record"
                                        ? "shadow-xs bg-white font-semibold text-indigo-600"
                                        : "hover:text-gray-900"
                                }`}
                            >
                                <i className="fa-solid fa-microphone"></i> Record Live
                            </button>
                            <button
                                type="button"
                                onClick={() => setActiveTab("upload")}
                                className={`flex items-center justify-center gap-2 rounded-md px-3 py-2 transition-all ${
                                    activeTab === "upload"
                                        ? "shadow-xs bg-white font-semibold text-indigo-600"
                                        : "hover:text-gray-900"
                                }`}
                            >
                                <i className="fa-solid fa-cloud-arrow-up"></i> Upload File
                            </button>
                        </div>

                        {/* Tab Container */}
                        <div className="shadow-xs rounded-xl border border-gray-200 bg-white p-5">
                            {activeTab === "record" ? (
                                <div className="space-y-3 text-center">
                                    <p className="text-xs text-gray-500">
                                        Use your microphone to record a direct audio message.
                                    </p>
                                    <VoiceRecorder
                                        handleAudioUpload={onRecorderSubmit}
                                        handleReset={deleteAudioResponse}
                                    />
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    <label className="group flex h-32 w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 transition-all hover:bg-gray-100/80">
                                        <div className="flex flex-col items-center justify-center px-4 pb-6 pt-5 text-center">
                                            <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-indigo-50 transition-transform group-hover:scale-110">
                                                <i className="fa-solid fa-file-audio text-lg text-indigo-600"></i>
                                            </div>
                                            <p className="text-xs text-gray-600">
                                                <span className="font-semibold text-indigo-600">
                                                    Click to browse
                                                </span>{" "}
                                                or drag & drop file
                                            </p>
                                            <p className="mt-1 text-[11px] text-gray-400">
                                                Supports MP3, WAV audio formats
                                            </p>
                                        </div>
                                        <input
                                            type="file"
                                            accept=".mp3, .wav, audio/*"
                                            className="hidden"
                                            onChange={handleAudioFileUpload}
                                        />
                                    </label>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Active Player Status Box */}
                    {isAudioReady && (
                        <div className="animate-fadeIn space-y-3 rounded-xl border border-emerald-200 bg-emerald-50/70 p-4 transition-all">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <span className="flex h-2 w-2 animate-pulse rounded-full bg-emerald-500" />
                                    <span className="text-xs font-bold uppercase tracking-wide text-emerald-900">
                                        {selectedFileName || "Audio Ready for Submission"}
                                    </span>
                                </div>
                                <button
                                    type="button"
                                    onClick={deleteAudioResponse}
                                    className="flex items-center gap-1.5 text-xs font-semibold text-red-600 transition-colors hover:text-red-700"
                                >
                                    <i className="fa-solid fa-trash-can" /> Remove
                                </button>
                            </div>

                            <div className="shadow-xs rounded-lg border border-emerald-100 bg-white p-2.5">
                                <VoterReachoutAudioPlayer data={audio_response || buffer[0]?.path} />
                            </div>
                        </div>
                    )}

                    {/* Validation Error Message */}
                    {voiceSubmit && !isAudioReady && (
                        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-xs font-medium text-red-600">
                            <i className="fa-solid fa-circle-exclamation text-sm text-red-500"></i>
                            Please record or upload an audio file before saving.
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="border-t border-gray-100 pt-4">
                        <FormButtons
                            onSave={() => setVoiceSubmit(true)}
                            onCancel={() => voterReachOutStore.set_isSideOverlay(false)}
                            disabled={loading || !isAudioReady}
                        />
                    </div>
                </Form>
            </Formik>
        </div>
    );
});

export default AddEditVoiceCall;
